import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import cx from 'classnames';

import withControlled from '../form/with-controlled';

import './style.scss';

const ENTER_KEY = 13;

let textareaIdCounter = 0;
const getNextTextareaId = () => {
  textareaIdCounter += 1;
  return textareaIdCounter;
};

export const Textarea = ({
  className,
  isDisabled = false,
  isAutoExpand = false,
  submitOnEnter = false,
  showErrors = true,
  style = {},
  name,
  value,
  placeholder,
  text,
  errors,
  isInvalid,
  disabled,
  onKeyDown = () => {},
  onPaste = () => {},
  onChange = () => {},
  onSubmit = () => {},
  inputRef,
  ...props
}) => {
  const errorIdRef = useRef(`textarea-error-${getNextTextareaId()}`);
  const errorId = errorIdRef.current;

  const localRef = useRef();
  const actualRef = inputRef || localRef;

  // Auto-expand height dynamically based on scrollHeight
  useLayoutEffect(() => {
    if (isAutoExpand && actualRef.current) {
      actualRef.current.style.height = 'auto';
      // scrollHeight does not include borders under border-box. We add 2px (1px top border + 1px bottom border)
      // to the scrollHeight to match the total outer height of the textarea and prevent scrollbars from showing.
      actualRef.current.style.height = `${actualRef.current.scrollHeight + 2}px`;
    }
  }, [value, isAutoExpand]);

  const handleChange = useCallback((evt) => {
    onChange(evt.target.value);
  }, [onChange]);

  const handleKeyDown = useCallback((evt) => {
    if (submitOnEnter && evt.keyCode === ENTER_KEY && !evt.shiftKey) {
      evt.preventDefault();
      onSubmit();
    }
    onKeyDown(evt);
  }, [submitOnEnter, onSubmit, onKeyDown]);

  return (
    <>
      <textarea
        rows={isAutoExpand ? 1 : undefined}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={onPaste}
        disabled={isDisabled}
        name={name}
        className={cx('c-textarea', {
          'c-textarea--auto-expand': isAutoExpand,
          'c-textarea--invalid': isInvalid && !disabled,
          [className]: !!className,
        })}
        style={style}
        wrap="soft"
        placeholder={placeholder}
        ref={actualRef}
        value={value}
        spellCheck="true"
        aria-invalid={isInvalid && !disabled ? 'true' : undefined}
        aria-describedby={errors && showErrors && !disabled ? errorId : undefined}
        {...props}
      />
      {errors && showErrors && !disabled && (
        <div
          id={errorId}
          className={cx('c-input__error', {
            'c-textarea__error--relative ': isAutoExpand,
          })}
          role="alert"
        >
          {errors.message}
        </div>
      )}
    </>
  );
};

export const TextareaControlled = withControlled(Textarea);
